import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SessionProvider, useSession } from "@/store/session";
import { AppStoreProvider } from "@/store/AppStoreProvider";
import { PreferencesProvider } from "@/store/preferences";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PAID_PLANS_ENABLED } from "@/config/features";
import PushNavigationBridge from "@/components/PushNavigationBridge";
import FreePlanAutoComplete from "@/components/FreePlanAutoComplete";
import BasketballLoader from "@/components/feed/BasketballLoader";

// Eager: entry points and the default authenticated screen.
import Welcome from "./pages/Welcome";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import Feed from "./pages/Feed";

// Lazy: every secondary route is code-split to keep the initial bundle small.
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const Profile = lazy(() => import("./pages/Profile"));
const Conversation = lazy(() => import("./pages/Conversation"));
const Offers = lazy(() => import("./pages/Offers"));
const CreateOffer = lazy(() => import("./pages/CreateOffer"));
const OfferDetail = lazy(() => import("./pages/OfferDetail"));
const EditOffer = lazy(() => import("./pages/EditOffer"));
const MyApplications = lazy(() => import("./pages/MyApplications"));
const ClubList = lazy(() => import("./pages/ClubList"));
const ClubDetail = lazy(() => import("./pages/ClubDetail"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const Notifications = lazy(() => import("./pages/Notifications"));
const CreatePost = lazy(() => import("./pages/CreatePost"));
const Explore = lazy(() => import("./pages/Explore"));
const Messages = lazy(() => import("./pages/Messages"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const Subscription = lazy(() => import("./pages/Subscription"));
const CreateProfile = lazy(() => import("./pages/CreateProfile"));
const SelectPlan = lazy(() => import("./pages/SelectPlan"));
const CreateClub = lazy(() => import("./pages/CreateClub"));
const EditClub = lazy(() => import("./pages/EditClub"));
const ClubVerification = lazy(() => import("./pages/ClubVerification"));
const CoachVerification = lazy(() => import("./pages/CoachVerification"));
const ReportProblem = lazy(() => import("./pages/ReportProblem"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Support = lazy(() => import("./pages/Support"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const DeleteAccountRequest = lazy(() => import("./pages/DeleteAccountRequest"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmailPending = lazy(() => import("./pages/VerifyEmailPending"));
const VerifyEmailConfirm = lazy(() => import("./pages/VerifyEmailConfirm"));
const AccountSuspended = lazy(() => import("./pages/AccountSuspended"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <BasketballLoader />
  </div>
);


const AppRoutes = () => {
  const { currentUserId, currentUser, logout } = useSession();

  // Defensive: a stale session id with no matching profile would otherwise crash
  // downstream stores. Log it and force the user back to the welcome screen.
  if (currentUserId && !currentUser) {
    console.warn('[Session] currentUserId set but profile not found — logging out', currentUserId);
    logout();
  }

  if (!currentUserId || !currentUser) {
    return (
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email-confirm" element={<VerifyEmailConfirm />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {PAID_PLANS_ENABLED && <Route path="/select-plan" element={<SelectPlan />} />}
        <Route path="/create-profile" element={<CreateProfile />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/support" element={<Support />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/delete-account" element={<DeleteAccountRequest />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/legal" element={<LegalNotice />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    );
  }

  // Gate: suspended (banned) accounts get no app access at all.
  if (currentUser && (currentUser as { banned?: boolean }).banned) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/support" element={<Support />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={<AccountSuspended />} />
        </Routes>
      </Suspense>
    );
  }


  // Gate: mandatory email verification before any app access
  if (currentUser && currentUser.emailVerified === false) {
    return (
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/support" element={<Support />} />
        <Route path="/verify-email" element={<VerifyEmailPending />} />
        <Route path="/verify-email-confirm" element={<VerifyEmailConfirm />} />

        <Route path="*" element={<Navigate to="/verify-email" replace />} />
      </Routes>
      </Suspense>
    );
  }

  // Gate: force onboarding completion before app access
  if (currentUser && !currentUser.profileCompleted) {
    // Profile not yet built → send to create-profile first
    if (!currentUser.profileBuilt) {
      return (
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/support" element={<Support />} />
            <Route path="/create-profile" element={<CreateProfile />} />
            <Route path="*" element={<Navigate to="/create-profile" replace />} />

          </Routes>
        </Suspense>
      );
    }
    // Profile built but plan not selected → send to select-plan.
    // In the free-only V1 the plan step is skipped and the free plan is
    // confirmed automatically (see FreePlanAutoComplete).
    if (!PAID_PLANS_ENABLED) {
      return <FreePlanAutoComplete userId={currentUser.id} />;
    }
    return (
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/select-plan" element={<SelectPlan />} />
        <Route path="*" element={<Navigate to="/select-plan" replace />} />
      </Routes>
      </Suspense>
    );
  }


  return (
    <AppStoreProvider>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Feed />} />
        <Route path="/profile/:userId" element={<Profile />} />
        <Route path="/conversation/:threadId" element={<Conversation />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/offers/create" element={<CreateOffer />} />
        <Route path="/offers/:offerId" element={<OfferDetail />} />
        <Route path="/offers/:offerId/edit" element={<EditOffer />} />
        <Route path="/my-applications" element={<MyApplications />} />
        <Route path="/club" element={<ClubList />} />
        <Route path="/club/create" element={<CreateClub />} />
        <Route path="/club/:clubId" element={<ClubDetail />} />
        <Route path="/club/:clubId/edit" element={<EditClub />} />
        <Route path="/club/verification" element={<ClubVerification />} />
        <Route path="/coach/verification" element={<CoachVerification />} />
        <Route path="/club/:clubId/team/:teamId" element={<TeamDetail />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/create-post" element={<CreatePost />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/report" element={<ReportProblem />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/support" element={<Support />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/legal" element={<LegalNotice />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email-confirm" element={<VerifyEmailConfirm />} />
        <Route path="/admin" element={<AdminPanel />} />
        {/* V1 ships free-only: billing screens stay in the codebase but are
            unreachable while PAID_PLANS_ENABLED is false. */}
        <Route
          path="/subscription"
          element={PAID_PLANS_ENABLED ? <Subscription /> : <Navigate to="/settings" replace />}
        />
        <Route path="/create-profile" element={<CreateProfile />} />
        <Route
          path="/select-plan"
          element={PAID_PLANS_ENABLED ? <SelectPlan /> : <Navigate to="/" replace />}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </AppStoreProvider>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <PreferencesProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SessionProvider>
              <ErrorBoundary>
                <PushNavigationBridge />
                <AppRoutes />
              </ErrorBoundary>
            </SessionProvider>
          </BrowserRouter>
        </PreferencesProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
